import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import { useAuth } from '../contexts/AuthContext'
import { FiEye, FiEyeOff, FiLoader } from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { trackSignIn } from '../services/analytics'
import { hapticSuccess, hapticError } from '../services/hapticsService'
import Logo from '../components/common/Logo'
import AppleSignInButton from '../components/auth/AppleSignInButton'
import { isApplePlatform } from '../services/platformService'
import { stashInviteCodeFromUrl } from '../utils/pendingGroupInvite'

function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn, signUp } = useAuth()

  // A returning user arriving from a /join/:code invite link may sign in
  // rather than sign up, so stash the code here too. Consumed by the join
  // flow, never auto-redeemed.
  useEffect(() => {
    stashInviteCodeFromUrl();
  }, []);

  // Add auth-page class for iOS safe area styling
  useEffect(() => {
    document.documentElement.classList.add('auth-page');
    document.body.classList.add('auth-page');
    return () => {
      document.documentElement.classList.remove('auth-page');
      document.body.classList.remove('auth-page');
    };
  }, []);

  const prefillEmail = location.state?.prefillEmail || ''
  const fromEmailConfirmation = location.state?.fromEmailConfirmation || false

  const [formData, setFormData] = useState({
    email: prefillEmail,
    password: '',
    rememberMe: true
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(() => {
    try {
      const globalError = window.lastLoginError || ''
      const persistedError = sessionStorage.getItem('login_error') || ''
      return globalError || persistedError
    } catch (e) {
      return ''
    }
  })
  const [showPassword, setShowPassword] = useState(false)
  const [verificationError, setVerificationError] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  useEffect(() => {
    const checkForPendingError = () => {
      try {
        const globalError = window.lastLoginError || ''
        if (globalError && globalError !== error) {
          setError(globalError)
        }
      } catch (e) {
        // Ignore errors
      }
    }

    checkForPendingError()
    const timeoutId = setTimeout(checkForPendingError, 100)

    return () => {
      clearTimeout(timeoutId)
      if (!window.loginAttemptInProgress) {
        try {
          sessionStorage.removeItem('login_error')
          window.lastLoginError = ''
        } catch {
          // Ignore errors
        }
      }
    }
  }, [])

  const setErrorWithPersistence = (errorMessage) => {
    if (errorMessage) hapticError()
    setError(errorMessage)
    window.lastLoginError = errorMessage

    try {
      if (errorMessage) {
        sessionStorage.setItem('login_error', errorMessage)
      } else {
        sessionStorage.removeItem('login_error')
        window.lastLoginError = ''
      }
    } catch (e) {
      // Ignore sessionStorage errors
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    if (error) setErrorWithPersistence('')
    if (verificationError) setVerificationError(false)
    if (resendSuccess) setResendSuccess(false)
  }

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault()
    }

    window.loginAttemptInProgress = true

    if (!formData.email || !formData.password) {
      setErrorWithPersistence('Enter email and password')
      return
    }

    setLoading(true)
    setErrorWithPersistence('')
    setVerificationError(false)
    setResendSuccess(false)

    try {
      const { data, error } = await signIn(formData.email, formData.password)

      if (error) {
        if (error.message.includes('email not confirmed') ||
            error.message.includes('Email not confirmed') ||
            error.message.includes('email_not_confirmed') ||
            error.message.includes('signup_disabled') ||
            error.message.includes('email_confirmation_required')) {
          setVerificationError(true)
          setErrorWithPersistence('')
        } else {
          let errorMessage = error.message

          if (error.message.includes('Invalid login credentials') ||
              error.message.includes('invalid credentials') ||
              error.message.includes('Invalid email or password') ||
              error.message.includes('incorrect password') ||
              error.message.includes('invalid email') ||
              error.message.includes('user not found')) {
            errorMessage = 'Invalid email or password'
          } else if (error.message.includes('Too many requests') ||
                     error.message.includes('rate limit')) {
            errorMessage = 'Too many attempts. Try again later.'
          } else if (error.message.includes('network') ||
                     error.message.includes('connection')) {
            errorMessage = 'Connection error. Check your internet.'
          }

          setErrorWithPersistence(errorMessage)
          setVerificationError(false)
        }
      } else {
        if (data?.user?.id) {
          trackSignIn(data.user.id)
          hapticSuccess()
        }
        navigate('/')
      }
    } catch (err) {
      console.error('Unexpected error during sign in:', err)
      setErrorWithPersistence('An error occurred. Try again.')
    } finally {
      setLoading(false)
      window.loginAttemptInProgress = false
    }
  }

  const handleResendVerification = async () => {
    if (!formData.email) {
      setErrorWithPersistence('Enter your email address')
      return
    }

    setResendLoading(true)
    setErrorWithPersistence('')

    try {
      const { error } = await signUp(formData.email, 'tempPassword123', {
        first_name: 'User',
        last_name: 'Verification',
        resend: true
      })

      if (error) {
        if (error.message.includes('already registered') || error.message.includes('already exists')) {
          setResendSuccess(true)
        } else {
          setErrorWithPersistence('Failed to resend verification email')
        }
      } else {
        setResendSuccess(true)
      }
    } catch (err) {
      setErrorWithPersistence('Failed to resend verification email')
    } finally {
      setResendLoading(false)
    }
  }

  const goToEmailConfirmation = () => {
    navigate('/email-confirmation', {
      state: {
        email: formData.email,
        userData: { resend: true }
      }
    })
  }

  return (
    <div className="min-h-screen bg-app flex items-center justify-center p-4 safe-area-inset-top">
      <SEO
        title="Sign In | TeeSync"
        description="Sign in to TeeSync to manage your golf groups, view upcoming rounds, and coordinate tee times with your playing partners."
        canonical="/login"
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-md w-full"
      >
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center mx-auto mb-6">
            <Logo size="lg" variant="white" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary">
            Sign In
          </h1>
        </div>

        {/* Login Form */}
        <div className="bg-surface rounded-lg border border-border-subtle shadow-card p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message */}
            {error && !verificationError && (
              <div className="bg-status-error/10 border border-status-error/30 text-status-error px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Email Verification Required */}
            {verificationError && (
              <div className="bg-status-warning/10 border border-status-warning/30 rounded-md p-4">
                <h3 className="text-status-warning font-medium mb-2">
                  Email verification required
                </h3>
                <p className="text-status-warning text-sm mb-4">
                  Check your inbox for a confirmation link.
                </p>

                {resendSuccess && (
                  <div className="bg-accent/10 border border-accent/30 rounded-md p-3 mb-4">
                    <span className="text-sm text-accent">
                      Verification email sent
                    </span>
                  </div>
                )}

                <div className="flex flex-col xs:flex-row gap-3">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="flex items-center justify-center px-4 py-2 bg-status-warning text-white rounded-md hover:brightness-90 disabled:opacity-50 text-sm font-medium"
                  >
                    {resendLoading ? (
                      <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                    ) : (
                      'Resend Email'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={goToEmailConfirmation}
                    className="flex items-center justify-center px-4 py-2 border border-status-warning/30 text-status-warning rounded-md hover:bg-status-warning/10 text-sm font-medium"
                  >
                    View Instructions
                  </button>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent bg-surface text-text-primary placeholder-text-muted"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-secondary mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 pr-12 border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent bg-surface text-text-primary placeholder-text-muted text-base font-sans"
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-secondary"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                  className="w-4 h-4 text-accent border-border-subtle rounded focus:ring-accent"
                />
                <span className="text-sm text-text-secondary">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-accent hover:underline font-medium"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? (
                <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {isApplePlatform() && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-border-subtle" />
                <span className="text-xs uppercase tracking-wide text-text-muted">or</span>
                <div className="flex-1 h-px bg-border-subtle" />
              </div>

              <AppleSignInButton
                mode="signin"
                onError={(msg) => setErrorWithPersistence(msg)}
              />
            </>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/signup"
              className="text-sm text-accent hover:underline font-medium"
            >
              Create Account
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default Login
